import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  makeStyles
} from "@material-ui/core";

import SearchIcon from "@material-ui/icons/Search";
import PaymentIcon from "@material-ui/icons/Payment";
import VisibilityIcon from "@material-ui/icons/Visibility";
import CloseIcon from "@material-ui/icons/Close";
import RefreshIcon from "@material-ui/icons/Refresh";

import { useAlert } from "react-alert";

import {
  AccountReceivableCreatePayment,
  AccountReceivableFindAll,
  AccountReceivableFindOne
} from "../../../adminModel/accountReceivable";

const useStyles = makeStyles((theme) => ({
  root: { width: "100%" },
  card: {
    border: "1px solid #e5e7eb",
    borderRadius: 10,
    boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
    overflow: "hidden"
  },
  cardHeader: {
    backgroundColor: "#fafafa",
    borderBottom: "1px solid #eeeeee"
  },
  title: { fontSize: 16, fontWeight: 700, color: "#1f2937" },
  subtitle: { marginTop: 4, fontSize: 12, color: "#6b7280" },
  search: {
    width: "100%",
    "& .MuiOutlinedInput-root": { borderRadius: 8 }
  },
  tableHead: { backgroundColor: "#f7f7f7" },
  tableHeadCell: {
    fontSize: 12,
    fontWeight: 700,
    color: "#374151",
    whiteSpace: "nowrap",
    borderBottom: "1px solid #e5e7eb"
  },
  tableCell: {
    fontSize: 13,
    color: "#374151",
    whiteSpace: "nowrap",
    borderBottom: "1px solid #f0f0f0"
  },
  summaryCard: {
    padding: theme.spacing(1.75),
    border: "1px solid #e5e7eb",
    borderRadius: 9,
    backgroundColor: "#fff"
  },
  summaryLabel: { fontSize: 11, color: "#6b7280", marginBottom: 4 },
  summaryValue: { fontSize: 17, fontWeight: 700, color: "#111827" },
  status: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 82,
    padding: "5px 9px",
    borderRadius: 999,
    fontSize: 11,
    fontWeight: 700
  },
  statusUnpaid: { backgroundColor: "#fee2e2", color: "#991b1b" },
  statusPartial: { backgroundColor: "#fef3c7", color: "#92400e" },
  statusPaid: { backgroundColor: "#dcfce7", color: "#166534" },
  empty: { padding: theme.spacing(6), textAlign: "center", color: "#6b7280" },
  detailBox: {
    padding: theme.spacing(1.5),
    border: "1px solid #e5e7eb",
    borderRadius: 8,
    backgroundColor: "#fafafa"
  },
  detailLabel: { fontSize: 11, color: "#6b7280" },
  detailValue: { fontSize: 14, fontWeight: 700, color: "#111827", marginTop: 2 }
}));

const formatMoney = (value) =>
  Number(value || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit"
  });
};

const formatDateTimeLocal = (value) => {
  const date = value ? new Date(value) : new Date();
  if (Number.isNaN(date.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const statusClass = (classes, status) => {
  const s = String(status || "UNPAID").toUpperCase();
  if (s === "PAID") return `${classes.status} ${classes.statusPaid}`;
  if (s === "PARTIAL") return `${classes.status} ${classes.statusPartial}`;
  return `${classes.status} ${classes.statusUnpaid}`;
};

const StatusBadge = ({ classes, status }) => (
  <Box component="span" className={statusClass(classes, status)}>
    {String(status || "UNPAID").toUpperCase()}
  </Box>
);

const PaymentDialog = ({ open, invoice, onClose, onSaved }) => {
  const classes = useStyles();
  const alert = useAlert();

  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [amount, setAmount] = useState("");
  const [paymentDate, setPaymentDate] = useState(formatDateTimeLocal(new Date()));
  const [paymentMethod, setPaymentMethod] = useState("");
  const [referenceNo, setReferenceNo] = useState("");
  const [remarks, setRemarks] = useState("");

  const loadDetails = useCallback(async () => {
    if (!invoice?.id) return;
    setLoading(true);
    try {
      const result = await AccountReceivableFindOne(invoice.id);
      if (!result?.S) {
        alert.error(result?.msg || result?.errors || "Unable to load invoice details.");
        return;
      }
      setDetails(result.data || null);
    } catch (error) {
      alert.error(error?.message || "Unable to load invoice details.");
    } finally {
      setLoading(false);
    }
  }, [alert, invoice]);

  useEffect(() => {
    if (!open || !invoice?.id) return;
    setDetails(null);
    setAmount("");
    setPaymentDate(formatDateTimeLocal(new Date()));
    setPaymentMethod("");
    setReferenceNo("");
    setRemarks("");
    loadDetails();
  }, [open, invoice, loadDetails]);

  const current = details || invoice || {};
  const total = Number(current.total_amount || 0);
  const paid = Number(current.paid_amount || 0);
  const balance = Number(current.balance_amount || 0);
  const payments = Array.isArray(current.payments) ? current.payments : [];

  const handleSave = async () => {
    const paymentAmount = Number(amount);

    if (!current.id) {
      alert.error("Invoice ID is missing.");
      return;
    }
    if (!Number.isFinite(paymentAmount) || paymentAmount <= 0) {
      alert.error("Enter a valid payment amount.");
      return;
    }
    if (paymentAmount > balance) {
      alert.error(`Payment cannot exceed the remaining balance of ₱${formatMoney(balance)}.`);
      return;
    }
    if (!paymentMethod) {
      alert.error("Please select a payment method.");
      return;
    }

    setSaving(true);
    try {
      const result = await AccountReceivableCreatePayment({
        invoice_id: current.id,
        amount: paymentAmount,
        payment_date: paymentDate || new Date(),
        payment_method: paymentMethod,
        reference_no: referenceNo,
        remarks
      });

      if (!result?.S) {
        alert.error(result?.msg || result?.errors || "Unable to save payment.");
        return;
      }

      alert.success(result?.msg || "Payment successfully recorded.");
      await loadDetails();
      if (onSaved) await onSaved();

      setAmount("");
      setPaymentMethod("");
      setReferenceNo("");
      setRemarks("");
      setPaymentDate(formatDateTimeLocal(new Date()));
    } catch (error) {
      alert.error(error?.message || "Unable to save payment.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="h6">Payment</Typography>
            <Typography variant="body2" color="textSecondary">
              {current.invoice_no ? `Invoice ${current.invoice_no}` : "Account Receivable"}
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose} disabled={saving}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Box className={classes.detailBox}>
              <Typography className={classes.detailLabel}>Customer</Typography>
              <Typography className={classes.detailValue}>{current.name || "-"}</Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={2}>
            <Box className={classes.detailBox}>
              <Typography className={classes.detailLabel}>Total</Typography>
              <Typography className={classes.detailValue}>₱{formatMoney(total)}</Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={2}>
            <Box className={classes.detailBox}>
              <Typography className={classes.detailLabel}>Paid</Typography>
              <Typography className={classes.detailValue}>₱{formatMoney(paid)}</Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={2}>
            <Box className={classes.detailBox}>
              <Typography className={classes.detailLabel}>Balance</Typography>
              <Typography className={classes.detailValue}>₱{formatMoney(balance)}</Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={2}>
            <Box className={classes.detailBox}>
              <Typography className={classes.detailLabel}>Status</Typography>
              <Box mt={0.5}>
                <StatusBadge classes={classes} status={current.payment_status} />
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              label="Amount to Pay"
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              inputProps={{ min: 0, max: balance, step: "0.01" }}
              helperText={`Remaining balance: ₱${formatMoney(balance)}`}
              disabled={saving || loading || balance <= 0}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              label="Payment Date"
              type="datetime-local"
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={saving || loading}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              select
              fullWidth
              size="small"
              variant="outlined"
              label="Payment Method"
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
              disabled={saving || loading || balance <= 0}
            >
              <MenuItem value="">Select payment method</MenuItem>
              <MenuItem value="CASH">Cash</MenuItem>
              <MenuItem value="CHECK">Check</MenuItem>
              <MenuItem value="BANK TRANSFER">Bank Transfer</MenuItem>
              <MenuItem value="CREDIT CARD">Credit Card</MenuItem>
              <MenuItem value="GCASH">GCash</MenuItem>
              <MenuItem value="OFFSET">Offset</MenuItem>
              <MenuItem value="POST DATED CHECK">Post dated check</MenuItem>
              <MenuItem value="OTHER">Other</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              label="Reference No."
              value={referenceNo}
              onChange={(e) => setReferenceNo(e.target.value)}
              disabled={saving || loading || balance <= 0}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              label="Remarks"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              disabled={saving || loading || balance <= 0}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 8 }}>
              Payment History
            </Typography>

            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Method</TableCell>
                    <TableCell>Reference</TableCell>
                    <TableCell>Remarks</TableCell>
                    <TableCell align="right">Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <CircularProgress size={22} />
                      </TableCell>
                    </TableRow>
                  ) : payments.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        No payment history.
                      </TableCell>
                    </TableRow>
                  ) : (
                    payments.map((payment) => (
                      <TableRow key={payment.id}>
                        <TableCell>{formatDate(payment.payment_date)}</TableCell>
                        <TableCell>{payment.payment_method || "-"}</TableCell>
                        <TableCell>{payment.reference_no || "-"}</TableCell>
                        <TableCell>{payment.remarks || "-"}</TableCell>
                        <TableCell align="right">₱{formatMoney(payment.amount)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Close</Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <PaymentIcon />}
          onClick={handleSave}
          disabled={saving || loading || balance <= 0}
        >
          {saving ? "Saving..." : "Save Payment"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const View = () => {
  const classes = useStyles();
  const alert = useAlert();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [start] = useState(0);
  const limit = 20;

  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const loadAR = useCallback(async () => {
    setLoading(true);
    try {
      const result = await AccountReceivableFindAll(search, start, limit);

      if (!result?.S) {
        alert.error(result?.msg || result?.errors || "Unable to load account receivable.");
        setRows([]);
        return;
      }

      setRows(Array.isArray(result.data) ? result.data : []);
    } catch (error) {
      alert.error(error?.message || "Unable to load account receivable.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [alert, search, start]);

  useEffect(() => {
    loadAR();
  }, []); // initial load

  const totals = useMemo(
    () =>
      rows.reduce(
        (acc, row) => {
          acc.total += Number(row.total_amount || 0);
          acc.paid += Number(row.paid_amount || 0);
          acc.balance += Number(row.balance_amount || 0);
          return acc;
        },
        { total: 0, paid: 0, balance: 0 }
      ),
    [rows]
  );

  const unpaidCount = rows.filter(
    (row) => String(row.payment_status).toUpperCase() === "UNPAID"
  ).length;

  const partialCount = rows.filter(
    (row) => String(row.payment_status).toUpperCase() === "PARTIAL"
  ).length;

  const handleOpen = (row) => {
    setSelectedInvoice(row);
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
    setSelectedInvoice(null);
  };

  const handleSaved = async () => {
    await loadAR();
  };

  return (
    <Box className={classes.root}>
      <Card className={classes.card}>
        <CardHeader
          className={classes.cardHeader}
          title={
            <Box>
              <Typography className={classes.title}>Account Receivable</Typography>
              <Typography className={classes.subtitle}>
                Outstanding customer invoices and balances
              </Typography>
            </Box>
          }
        />

        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Box className={classes.summaryCard}>
                <Typography className={classes.summaryLabel}>Outstanding Amount</Typography>
                <Typography className={classes.summaryValue}>₱{formatMoney(totals.balance)}</Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box className={classes.summaryCard}>
                <Typography className={classes.summaryLabel}>Unpaid Invoices</Typography>
                <Typography className={classes.summaryValue}>{unpaidCount}</Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box className={classes.summaryCard}>
                <Typography className={classes.summaryLabel}>Partial Invoices</Typography>
                <Typography className={classes.summaryValue}>{partialCount}</Typography>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Box display="flex" alignItems="center" gridGap={8}>
                <TextField
                  className={classes.search}
                  variant="outlined"
                  size="small"
                  placeholder="Search invoice no., customer or email..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") loadAR();
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    )
                  }}
                />

                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={loadAR}
                  disabled={loading}
                >
                  Refresh
                </Button>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead className={classes.tableHead}>
                    <TableRow>
                      <TableCell className={classes.tableHeadCell}>Invoice No.</TableCell>
                      <TableCell className={classes.tableHeadCell}>Customer</TableCell>
                      <TableCell className={classes.tableHeadCell}>Date</TableCell>
                      <TableCell align="right" className={classes.tableHeadCell}>Total</TableCell>
                      <TableCell align="right" className={classes.tableHeadCell}>Paid</TableCell>
                      <TableCell align="right" className={classes.tableHeadCell}>Balance</TableCell>
                      <TableCell className={classes.tableHeadCell}>Status</TableCell>
                      <TableCell align="center" className={classes.tableHeadCell}>Action</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center">
                          <Box py={5}>
                            <CircularProgress size={24} />
                          </Box>
                        </TableCell>
                      </TableRow>
                    ) : rows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className={classes.empty}>
                          No outstanding account receivable found.
                        </TableCell>
                      </TableRow>
                    ) : (
                      rows.map((row) => (
                        <TableRow key={row.id}>
                          <TableCell className={classes.tableCell}>
                            <Typography style={{ fontWeight: 700, fontSize: 13 }}>
                              {row.invoice_no || "-"}
                            </Typography>
                          </TableCell>

                          <TableCell className={classes.tableCell}>
                            <Typography style={{ fontSize: 13, fontWeight: 600 }}>
                              {row.name || "-"}
                            </Typography>
                            {row.email && (
                              <Typography variant="caption" color="textSecondary">
                                {row.email}
                              </Typography>
                            )}
                          </TableCell>

                          <TableCell className={classes.tableCell}>
                            {formatDate(row.created_at)}
                          </TableCell>

                          <TableCell align="right" className={classes.tableCell}>
                            ₱{formatMoney(row.total_amount)}
                          </TableCell>

                          <TableCell align="right" className={classes.tableCell}>
                            ₱{formatMoney(row.paid_amount)}
                          </TableCell>

                          <TableCell align="right" className={classes.tableCell}>
                            <Typography style={{ fontWeight: 700 }}>
                              ₱{formatMoney(row.balance_amount)}
                            </Typography>
                          </TableCell>

                          <TableCell className={classes.tableCell}>
                            <StatusBadge classes={classes} status={row.payment_status} />
                          </TableCell>

                          <TableCell align="center" className={classes.tableCell}>
                            <Box display="flex" justifyContent="center" gridGap={4}>
                              <IconButton
                                size="small"
                                title="View payment details"
                                onClick={() => handleOpen(row)}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>

                              <Button
                                size="small"
                                variant="contained"
                                color="primary"
                                startIcon={<PaymentIcon />}
                                onClick={() => handleOpen(row)}
                              >
                                Pay
                              </Button>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <PaymentDialog
        open={dialogOpen}
        invoice={selectedInvoice}
        onClose={handleClose}
        onSaved={handleSaved}
      />
    </Box>
  );
};

export default View;
